import {
  Smile,
  Meh,
  AlertCircle,
  Frown,
  Angry,
  Zap,
  HelpCircle,
  AlarmClock,
  Ellipsis,
  Siren,
  CalendarCheck,
  PhoneForwarded,
  MessageCircleQuestion,
  ThumbsDown,
  CalendarClock,
  CalendarX,
  FileText,
  Receipt,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { normalizeSentiment, normalizeIntent } from '../../lib/callSemantics';

const SENTIMENT_ICONS = {
  happy: Smile,
  neutral: Meh,
  worried: AlertCircle,
  upset: Frown,
  angry: Angry,
  frustrated: Zap,
  confused: HelpCircle,
  urgent: AlarmClock,
  other: Ellipsis,
};

const INTENT_ICONS = {
  emergency: Siren,
  booking: CalendarCheck,
  callback: PhoneForwarded,
  enquiry: MessageCircleQuestion,
  complaint: ThumbsDown,
  reschedule: CalendarClock,
  cancellation: CalendarX,
  quote: FileText,
  billing: Receipt,
  other: Ellipsis,
};

const SENTIMENT_RING = {
  happy: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
  worried: 'bg-amber-50 text-amber-800 ring-amber-200',
  upset: 'bg-orange-50 text-orange-800 ring-orange-200',
  angry: 'bg-red-50 text-red-700 ring-red-200',
  frustrated: 'bg-rose-50 text-rose-700 ring-rose-200',
  confused: 'bg-violet-50 text-violet-700 ring-violet-200',
  urgent: 'bg-red-50 text-red-800 ring-red-300',
  other: 'bg-slate-100 text-slate-600 ring-slate-200',
};

const INTENT_RING = {
  emergency: 'bg-red-50 text-red-700 ring-red-200',
  booking: 'bg-blue-50 text-blue-700 ring-blue-200',
  callback: 'bg-teal-50 text-teal-700 ring-teal-200',
  enquiry: 'bg-cyan-50 text-cyan-800 ring-cyan-200',
  complaint: 'bg-orange-50 text-orange-800 ring-orange-200',
  reschedule: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  cancellation: 'bg-slate-100 text-slate-700 ring-slate-200',
  quote: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  billing: 'bg-amber-50 text-amber-900 ring-amber-200',
  other: 'bg-slate-100 text-slate-600 ring-slate-200',
};

const readable = (key) => key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');

export function SentimentSemanticsIcon({ rawValue, size = 16, className }) {
  if (rawValue === undefined || rawValue === null || `${rawValue}`.trim() === '') return null;
  const normalized = normalizeSentiment(rawValue);
  const Icon = SENTIMENT_ICONS[normalized] || Ellipsis;
  const rawSlug = String(rawValue).trim().toLowerCase().replace(/[\s-]+/g, '_');
  const tooltipBody =
    rawSlug && rawSlug !== normalized
      ? `Customer sentiment: ${readable(normalized)} (${rawValue})`
      : `Customer sentiment: ${readable(normalized)}`;

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={tooltipBody}
          className={cn(
            'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-1 ring-inset',
            SENTIMENT_RING[normalized] || SENTIMENT_RING.other,
            className
          )}
        >
          <Icon className="shrink-0" size={size} strokeWidth={2} aria-hidden />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left">
        <p className="font-medium">{tooltipBody}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export function IntentSemanticsIcon({ rawValue, size = 16, className }) {
  if (rawValue === undefined || rawValue === null || `${rawValue}`.trim() === '') return null;
  const normalized = normalizeIntent(rawValue);
  const Icon = INTENT_ICONS[normalized] || Ellipsis;
  const rawSlug = String(rawValue).trim().toLowerCase().replace(/[\s-]+/g, '_');
  const tooltipBody =
    rawSlug && rawSlug !== normalized
      ? `Intent: ${readable(normalized)} (${rawValue})`
      : `Intent: ${readable(normalized)}`;

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={tooltipBody}
          className={cn(
            'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-1 ring-inset',
            INTENT_RING[normalized] || INTENT_RING.other,
            className
          )}
        >
          <Icon className="shrink-0" size={size} strokeWidth={2} aria-hidden />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left">
        <p className="font-medium">{tooltipBody}</p>
      </TooltipContent>
    </Tooltip>
  );
}
